import api from '../api';

export type EmailTemplateKey =
  | 'EMAIL_TPL_REG_ATTENDEE'
  | 'EMAIL_TPL_REG_SPEAKER'
  | 'EMAIL_TPL_SPEAKER_APPROVED'
  | 'EMAIL_TPL_SPEAKER_REJECTED'
  | 'EMAIL_TPL_SPEAKER_OBSERVATIONS'
  | 'EMAIL_TPL_CREDENTIAL_ATTENDEE_PAID'
  | 'EMAIL_TPL_CREDENTIAL_ATTENDEE_FREE'
  | 'EMAIL_TPL_SPEAKER_PAID_UPLOAD'
  | 'EMAIL_TPL_CREDENTIAL_SPEAKER';

export type EmailTemplateSource = 'EVENT' | 'GLOBAL' | 'CODE';

export interface EmailTemplateCatalogItem {
  key: EmailTemplateKey;
  name: string;
  description: string;
  activeSource: EmailTemplateSource;
  hasGlobalTemplate: boolean;
  hasEventTemplate: boolean;
  eventId: string | null;
  eventName: string | null;
  placeholders: string[];
  subject: string;
  body: string;
}

export interface EmailTemplateEditorData {
  key: EmailTemplateKey;
  name: string;
  description: string;
  placeholders: string[];
  allowedScopes: Array<'GLOBAL' | 'EVENT'>;
  eventId: string | null;
  eventName: string | null;
  activeSource: EmailTemplateSource;
  current: { subject: string; body: string };
  globalTemplate: { subject: string; body: string } | null;
  eventTemplate: { subject: string; body: string } | null;
  fallbackTemplate: { subject: string; body: string };
}

export interface EmailTemplatePreview {
  to: string;
  templateKey: EmailTemplateKey;
  templateName: string;
  subject: string;
  html: string;
  sourceBody: string;
  placeholders: Record<string, string>;
  eventName: string;
}

type TemplateScope = 'GLOBAL' | 'EVENT';

export const emailTemplatesApi = {
  list: async (eventId?: string) => {
    const { data } = await api.get<EmailTemplateCatalogItem[]>('/email-templates', {
      params: eventId ? { eventId } : undefined,
    });
    return data;
  },

  get: async (key: EmailTemplateKey, eventId?: string) => {
    const { data } = await api.get<EmailTemplateEditorData>(`/email-templates/${encodeURIComponent(key)}`, {
      params: eventId ? { eventId } : undefined,
    });
    return data;
  },

  save: async (
    key: EmailTemplateKey,
    payload: { scope: TemplateScope; eventId?: string | null; subject: string; body: string },
  ) => {
    const { data } = await api.put(`/email-templates/${encodeURIComponent(key)}`, payload);
    return data;
  },

  restore: async (key: EmailTemplateKey, payload: { scope: TemplateScope; eventId?: string | null }) => {
    const { data } = await api.delete(`/email-templates/${encodeURIComponent(key)}`, { data: payload });
    return data;
  },

  preview: async (
    key: EmailTemplateKey,
    payload?: { eventId?: string | null; subject?: string; body?: string },
  ) => {
    const { data } = await api.post<EmailTemplatePreview>(`/email-templates/${encodeURIComponent(key)}/preview`, payload);
    return data;
  },

  sendTest: async (
    key: EmailTemplateKey,
    payload?: { eventId?: string | null; subject?: string; body?: string },
  ) => {
    const { data } = await api.post<{ message: string; preview: EmailTemplatePreview; deliveredTo: string }>(
      `/email-templates/${encodeURIComponent(key)}/test`,
      payload,
    );
    return data;
  },
};
